import type { FileSystemAPI } from "@webcontainer/api";
import type { GeneratedFileTemplate } from "schemas/generated/fileTemplate";

import { useEffect, useRef } from "react";

import { useWebContainer } from "./context";

const TEMPLATE_PREVIEW_PATH = "/app/template-preview";

async function insertTemplate(
  fs: FileSystemAPI,
  template: GeneratedFileTemplate,
) {
  const templatePath = `${TEMPLATE_PREVIEW_PATH}/${template.id}`;
  await fs.mkdir(templatePath, { recursive: true });

  const pageContent = `
import { ProductsLoader } from "@/features/product/products-loader";
import { CartLoader } from "@/features/cart/cart-loader";
import { AnalyticsProvider } from "@/features/analytics/analytics-provider";
import Component from "./client-component";

export default function Page() {
  return (
    <AnalyticsProvider>
      <ProductsLoader productIds={[]}>
        <CartLoader>
          <Component />
        </CartLoader>
      </ProductsLoader>
    </AnalyticsProvider>
  );
}`;
  await fs.writeFile(`${templatePath}/page.tsx`, pageContent);

  const clientComponentContent = `"use client";\n${template.content}`;
  await fs.writeFile(
    `${templatePath}/client-component.tsx`,
    clientComponentContent,
  );
}

async function removeTemplate(fs: FileSystemAPI, templateId: string) {
  const templatePath = `${TEMPLATE_PREVIEW_PATH}/${templateId}`;
  await fs.rm(templatePath, { recursive: true, force: true });
}

export function useWebContainerTemplate({
  template,
  enabled = true,
}: {
  template: GeneratedFileTemplate;
  enabled?: boolean;
}) {
  const { webContainerApi: webContainer } = useWebContainer();
  const fs = webContainer?.fs;
  const isMountedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !fs || isMountedRef.current) {
      return;
    }

    void insertTemplate(fs, template).then(() => {
      isMountedRef.current = true;
    });

    return () => {
      if (!fs || !isMountedRef.current) {
        return;
      }
      void removeTemplate(fs, template.id).then(() => {
        isMountedRef.current = false;
      });
    };
  }, [template, enabled, fs]);

  return {
    isInserted: isMountedRef.current,
    templateRoute: `/template-preview/${template.id}`,
  };
}
